"use client";

import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Field, Input, Textarea, Checkbox, FormStatus, SubmitButton } from "@/components/ui/Form";
import {
  fetchUserGallery,
  createUserGallery,
  updateUserGallery,
} from "@/lib/api/c-photo-gallery-user";
import styles from "./CPhotoGallery.module.css";

const schema = z.object({
  name: z.string().min(1, "Podaj nazwę galerii.").max(500, "Nazwa zbyt długa (max 500 znaków)."),
  text: z.string().max(2000, "Opis zbyt długi (max 2000 znaków)."),
  public: z.boolean(),
});

type FormValues = z.infer<typeof schema>;

/**
 * Formularz dodania / edycji galerii usera. `galleryId` podany -> tryb edycji
 * (fetch meta on mount). Po zapisie woła `onSaved(id)`.
 */
export function GalleryForm({
  galleryId,
  onSaved,
  onCancel,
}: {
  galleryId?: number;
  onSaved: (id: number) => void;
  onCancel: () => void;
}) {
  const isEdit = typeof galleryId === "number";
  const [loading, setLoading] = useState(isEdit);
  const [topError, setTopError] = useState<string | null>(null);

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: "onTouched",
    defaultValues: { name: "", text: "", public: true },
  });

  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { gallery } = await fetchUserGallery(galleryId!);
        if (cancelled) return;
        reset({ name: gallery.name, text: gallery.text, public: gallery.public === 1 });
      } catch (e) {
        if (!cancelled) setTopError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isEdit, galleryId, reset]);

  const onSubmit = async (values: FormValues) => {
    setTopError(null);
    try {
      if (isEdit) {
        await updateUserGallery(galleryId!, values);
        onSaved(galleryId!);
      } else {
        const { gallery_id } = await createUserGallery(values);
        onSaved(gallery_id);
      }
    } catch (e) {
      setTopError(e instanceof Error ? e.message : String(e));
    }
  };

  if (loading) {
    return <p className={styles.empty}>Wczytywanie...</p>;
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
      <h2 className={styles.formTitle}>{isEdit ? "Edytuj galerię" : "Nowa galeria"}</h2>

      <Field label="Nazwa galerii" required error={errors.name?.message}>
        <Input type="text" maxLength={500} disabled={isSubmitting} {...register("name")} />
      </Field>

      <Field label="Opis" error={errors.text?.message}>
        <Textarea rows={5} maxLength={2000} disabled={isSubmitting} {...register("text")} />
      </Field>

      {/* Controller (nie register) - Checkbox component nie pasuje do standardowego
          register ref; bez tego value `public` jest undefined i z.boolean() blokuje submit. */}
      <Controller
        name="public"
        control={control}
        render={({ field }) => (
          <Checkbox
            checked={!!field.value}
            onChange={(e) => field.onChange(e.target.checked)}
            disabled={isSubmitting}
          >
            Galeria publiczna (widoczna na stronie)
          </Checkbox>
        )}
      />

      {topError && <FormStatus variant="error">{topError}</FormStatus>}

      <div className={styles.formActions}>
        <button type="button" className={styles.btnSecondary} onClick={onCancel} disabled={isSubmitting}>
          Anuluj
        </button>
        <SubmitButton loading={isSubmitting}>
          {isEdit ? "Zapisz zmiany" : "Utwórz galerię"}
        </SubmitButton>
      </div>
    </form>
  );
}
